// ✅ src/pages/VehicleV2/PeriodicEntryReport_PrintList.jsx
//
// ✅ Whole PRINT LIST page (A4) with SAME header style as your screenshot
// ✅ Fetches the list again from backend using filters passed in URL query
//
// ✅ URL format (open this page from listing):
// /vehicle/PeriodicEntryReport_PrintList?
//   branchtype=HO&branchid=8&userid=31&
//   fromDate=2022-03-22&todate=2022-03-30&
//   branch=8&store=1&status=ServiceExpired&vehicleStatus=Select&
//   vehicleNo=MZ01&vehicleCode=1168&vehicleMainType=TIPPER%20MAIN
//
// ✅ Backend used:
// GET  /api/periodicEntryReport/list   (same as your report page)
//
// ------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/periodicEntryReport";

const toStr = (v) => String(v ?? "").trim();
const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};
const fmt2 = (v) =>
  toNum(v, 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pick = (...vals) => {
  for (const v of vals) {
    const s = toStr(v);
    if (s) return s;
  }
  return "";
};

export default function PeriodicEntryReport_PrintList() {
  const [sp] = useSearchParams();

  // ✅ session / auth values (passed in URL)
  const branchtype = toStr(sp.get("branchtype")) || "HO";
  const branchid = toStr(sp.get("branchid")) || "";
  const userid = toStr(sp.get("userid")) || "";

  // ✅ filters (passed in URL)
  const filters = useMemo(
    () => ({
      fromDate: toStr(sp.get("fromDate")),
      toDate: toStr(sp.get("toDate")),
      branch: toStr(sp.get("branch")) || "Select",
      store: toStr(sp.get("store")) || "Select",
      status: toStr(sp.get("status")) || "Select",
      vehicleStatus: toStr(sp.get("vehicleStatus")) || "Select",
      vehicleNo: toStr(sp.get("vehicleNo")),
      vehicleCode: toStr(sp.get("vehicleCode")),
      vehicleMainType: toStr(sp.get("vehicleMainType")) || "Select",
    }),
    [sp]
  );

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  const load = async () => {
    setLoading(true);
    setErr("");

    try {
      const params = {
        branchtype,
        branchid,
        userid,
        ...filters,
      };

      const { data } = await axios.get(`${API_BASE}/list`, { params });
      if (!data?.success) throw new Error(data?.message || "Failed to load print list");

      setRows(data.rows || []);
      setTimeout(() => window.print(), 450);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Server error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentDateTime = useMemo(() => new Date().toLocaleString("en-IN"), []);

  const summary = useMemo(() => {
    // try to show some header info from first row (like screenshot)
    const r0 = rows?.[0] || {};
    return {
      branch: pick(r0.fld_branchname, ""),
      type: pick(r0.VehicleType, r0.fld_vehicletypename, ""),
      subType: pick(r0.SubType, r0.fld_vehicle_subtypename, ""),
      equipmentCode: pick(r0.fld_vehicle_code, ""),
      regNo: pick(r0.fld_vehicle_regno, ""),
      name: pick(r0.fld_vehicle_name, ""),
    };
  }, [rows]);

  // Map each list row to print table columns like screenshot
  const tableRows = useMemo(() => {
    return (rows || []).map((r, idx) => {
      const lastChanged = r.fld_hrmtrreading ?? r.lastChanged ?? "";
      const usageLimit = r.fld_usagelimit_calc ?? r.fld_usagelimit ?? r.usageLimit ?? "";
      const nextCum = r.fld_usagelimit_calc ?? r.nextService ?? "";
      const currentCum = r.fld_hrskm ?? r.currentCumulative ?? "";
      const lastUpdateDate = pick(r.fld_progressentrydate, r.fld_curdate, r.updateDate, "");
      const remaining = r.fld_Remaininghr ?? r.remaining ?? "";
      const alertText = pick(r.alertText, r.alert, "");

      return {
        sno: idx + 1,
        vehicleCode: pick(r.fld_vehicle_code, r.code, ""),
        vehicleName: pick(r.fld_vehicle_name, r.name, ""),
        regNo: pick(r.fld_vehicle_regno, r.regNo, ""),
        subAssembly: pick(r.fld_vehicle_subassemblyname, r.subAssembly, ""),
        description: pick(r.fld_Description, r.description, ""),
        oilGradePartNo: pick(r.fld_partnumbergrade, r.oilGradePartNo, ""),
        lastChanged,
        usageLimit,
        nextCum,
        currentCum,
        lastUpdateDate,
        remaining,
        alert: alertText,
      };
    });
  }, [rows]);

  if (loading) {
    return (
      <div style={{ padding: 20, fontFamily: "Arial, sans-serif" }}>
        Loading print...
      </div>
    );
  }

  if (err) {
    return (
      <div style={{ padding: 20, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Print failed</div>
        <div style={{ color: "crimson" }}>{err}</div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh", fontFamily: "Arial, sans-serif" }}>
      {/* Print CSS */}
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display:none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          thead { display: table-header-group; }
        }

        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #000; padding: 6px; font-size: 11px; }
        th { font-weight: 700; text-align: center; }

        .outer { border: 2px solid #000; padding: 10px; }
        .box { border: 1px solid #000; }
        .title { text-align:center; font-weight:700; padding:8px 0; }
      `}</style>

      <div className="outer">
        {/* HEADER (same style as screenshot) */}
        <div className="box" style={{ padding: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>Nissi Infotech</div>
              <div style={{ fontWeight: 700, fontSize: 12, marginTop: 2 }}>Nissi</div>
              <div style={{ fontWeight: 700, fontSize: 12 }}>CIVIL CONSTRUCTION</div>

              <div style={{ fontSize: 11, marginTop: 6, lineHeight: 1.3 }}>
                <div>3/42-12, Nagappa Colony, kavundampalayam - 641030 . Tamil Nadu . India .</div>
                <div>Phone No : 9003744644</div>
                <div>infracnsoft-implementation@nissiinfotech.in</div>
              </div>
            </div>

            <div style={{ textAlign: "right", fontSize: 11, minWidth: 140 }}>
              <div style={{ fontWeight: 700 }}>Printed On</div>
              <div>{currentDateTime}</div>
              <div style={{ marginTop: 6 }}>
                <div style={{ fontWeight: 700 }}>Total Rows</div>
                <div>{rows.length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="box" style={{ borderTop: 0 }}>
          <div className="title">Periodic Entry Report (List)</div>
        </div>

        {/* Filter summary / top info */}
        <div className="box" style={{ borderTop: 0, padding: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, fontSize: 12 }}>
            <div style={{ lineHeight: 1.8 }}>
              <div>
                <b>From Date</b> : {filters.fromDate || "-"}
              </div>
              <div>
                <b>To Date</b> : {filters.toDate || "-"}
              </div>
              <div>
                <b>Vehicle Code</b> : {filters.vehicleCode || "-"}
              </div>
              <div>
                <b>Vehicle Reg No</b> : {filters.vehicleNo || "-"}
              </div>
            </div>

            <div style={{ lineHeight: 1.8 }}>
              <div>
                <b>Branch</b> : {summary.branch || filters.branch || "-"}
              </div>
              <div>
                <b>Type</b> : {summary.type || filters.vehicleMainType || "-"}
              </div>
              <div>
                <b>Sub Type</b> : {summary.subType || "-"}
              </div>
              <div>
                <b>Status</b> : {filters.status || "-"}
              </div>
            </div>
          </div>
        </div>

        {/* List Table */}
        <div className="box" style={{ borderTop: 0 }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>S.No</th>
                <th style={{ width: 85 }}>Vehicle Code</th>
                <th style={{ width: 130 }}>Vehicle Name</th>
                <th style={{ width: 110 }}>Vehicle Reg No</th>
                <th style={{ width: 95 }}>Sub Assembly</th>
                <th style={{ width: 130 }}>Description</th>
                <th style={{ width: 90 }}>Oil/Grade part no</th>
                <th style={{ width: 85 }}>Last change Hr/KM</th>
                <th style={{ width: 80 }}>Usage Limit</th>
                <th style={{ width: 95 }}>Next cummulative Hr/KM</th>
                <th style={{ width: 110 }}>Current cummulative Hr/KM</th>
                <th style={{ width: 90 }}>Last Update Date</th>
                <th style={{ width: 85 }}>Remaining Hrs/KM</th>
                <th style={{ width: 90 }}>Alert</th>
              </tr>
            </thead>

            <tbody>
              {tableRows.map((d) => (
                <tr key={d.sno}>
                  <td style={{ textAlign: "center" }}>{d.sno}</td>
                  <td style={{ textAlign: "center" }}>{d.vehicleCode}</td>
                  <td style={{ textAlign: "center" }}>{d.vehicleName}</td>
                  <td style={{ textAlign: "center" }}>{d.regNo}</td>
                  <td style={{ textAlign: "center" }}>{d.subAssembly}</td>
                  <td style={{ textAlign: "center" }}>{d.description}</td>
                  <td style={{ textAlign: "center" }}>{d.oilGradePartNo}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.lastChanged)}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.usageLimit)}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.nextCum)}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.currentCum)}</td>
                  <td style={{ textAlign: "center" }}>{d.lastUpdateDate}</td>
                  <td style={{ textAlign: "right" }}>{fmt2(d.remaining)}</td>
                  <td style={{ textAlign: "center" }}>{d.alert}</td>
                </tr>
              ))}

              {tableRows.length === 0 && (
                <tr>
                  <td colSpan={14} style={{ textAlign: "center", padding: 20 }}>
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* footer blank (optional) */}
          <div style={{ height: 20 }} />
        </div>

        {/* Footer note */}
        <div className="box" style={{ borderTop: 0, padding: 10, fontSize: 11 }}>
          Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
        </div>

        {/* Print button */}
        <div className="no-print" style={{ marginTop: 12, textAlign: "center" }}>
          <button
            onClick={() => window.print()}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "1px solid #333",
              cursor: "pointer",
              background: "#1d4ed8",
              color: "#fff",
              fontWeight: 700,
            }}
          >
            Print
          </button>
        </div>
      </div>
    </div>
  );
}
